import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Link } from "react-router-dom";
import { Lightbulb, ShieldCheck, Target, BadgeCheck, ArrowRight, Compass } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { IMAGES, SITE } from "@/config/site";
import Section, { RevealItem } from "@/components/Section";
import FloatingObjects from "@/components/FloatingObjects";
import PageHead from "@/components/PageHead";

gsap.registerPlugin(ScrollTrigger);

export default function About() {
  const { t, lang } = useLang();
  const blueprintRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Animate blueprint SVG lines drawing in
      const paths = blueprintRef.current?.querySelectorAll("path, line");
      if (paths) {
        paths.forEach((p) => {
          const len = p.getTotalLength ? p.getTotalLength() : 600;
          p.style.strokeDasharray = len;
          p.style.strokeDashoffset = len;
        });
        gsap.to(paths, {
          strokeDashoffset: 0,
          duration: 2.4,
          ease: "power2.out",
          stagger: 0.05,
          scrollTrigger: { trigger: blueprintRef.current, start: "top 80%" },
        });
      }
    });
    return () => ctx.revert();
  }, []);

  const values = [
    { Icon: Lightbulb, t: t.about.values[0].t, d: t.about.values[0].d },
    { Icon: BadgeCheck, t: t.about.values[1].t, d: t.about.values[1].d },
    { Icon: Target, t: t.about.values[2].t, d: t.about.values[2].d },
    { Icon: ShieldCheck, t: t.about.values[3].t, d: t.about.values[3].d },
  ];

  return (
    <>
      <PageHead customTitle={`${t.about.overline} · ${SITE.brand.en}`} customDesc={t.about.p1.slice(0, 160)} />

      {/* Header */}
      <section className="relative pt-40 pb-16 overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0 -z-10">
          <img src={IMAGES.electrical} alt="" className="w-full h-full object-cover opacity-25" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-900 via-navy-900/85 to-navy-900" />
          <div className="absolute inset-0 grid-blueprint opacity-40" />
          <FloatingObjects density="low" />
        </div>
        <div className="container-pad">
          <div className="max-w-3xl">
            <div className="text-overline mb-4">{t.about.overline}</div>
            <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05]">
              {t.about.title}
            </h1>
            <p className="mt-3 text-xl md:text-2xl text-metallic font-heading">
              {SITE.brand.ar}
            </p>
          </div>
        </div>
      </section>

      <Section testId="about-content" className="!py-16">
        <div className="container-pad grid lg:grid-cols-12 gap-12">
          <RevealItem className="lg:col-span-7">
            <p className="text-white/80 text-base md:text-lg leading-relaxed">{t.about.p1}</p>
            <div className="my-8 metal-strip max-w-xs" />
            <p className="text-white/80 text-base md:text-lg leading-relaxed">{t.about.p2}</p>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/services" className="btn-primary">
                {t.hero.ctaPrimary} <ArrowRight className="w-4 h-4 rtl-flip" />
              </Link>
              <Link to="/projects" className="btn-secondary">
                {lang === "ar" ? "اطلع على المشاريع" : "See Projects"}
              </Link>
            </div>
          </RevealItem>

          <RevealItem className="lg:col-span-5">
            <div className="relative aspect-square overflow-hidden border border-white/10">
              <img src={IMAGES.fabrication} alt="Industrial workshop" className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-tr from-navy-900/90 via-navy-900/30 to-transparent" />
              {/* Animated blueprint SVG overlay */}
              <svg
                ref={blueprintRef}
                viewBox="0 0 400 400"
                className="absolute inset-0 w-full h-full"
                fill="none"
                stroke="#D4AF37"
                strokeWidth="1.2"
                strokeOpacity="0.65"
                aria-hidden="true"
              >
                <line x1="20" y1="40" x2="380" y2="40" />
                <line x1="20" y1="40" x2="20" y2="360" />
                <line x1="380" y1="40" x2="380" y2="360" />
                <line x1="20" y1="360" x2="380" y2="360" />
                <line x1="200" y1="40" x2="200" y2="360" strokeDasharray="6 6" />
                <line x1="20" y1="200" x2="380" y2="200" strokeDasharray="6 6" />
                <path d="M80 200 L120 160 L200 240 L280 160 L320 200" />
                <path d="M120 320 L120 280 L200 280 L200 320" />
                <path d="M260 320 L260 280 L320 280 L320 320" />
                <path d="M200 80 m -28 0 a 28 28 0 1 0 56 0 a 28 28 0 1 0 -56 0" />
              </svg>
              <div className="absolute top-4 left-4 text-[10px] font-mono text-gold-500/80 tracking-widest">
                EAH · BLUEPRINT · 015
              </div>
            </div>
          </RevealItem>
        </div>
      </Section>

      {/* Values */}
      <Section testId="about-values">
        <div className="container-pad">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
            <div>
              <RevealItem className="text-overline mb-3">{lang === "ar" ? "قيمنا" : "Our Values"}</RevealItem>
              <RevealItem
                as="h2"
                className="font-heading font-black tracking-tight text-3xl md:text-4xl lg:text-5xl text-white leading-tight max-w-2xl"
              >
                {lang === "ar" ? "ما نُقدّمه في كل مشروع" : "What we bring to every project"}
              </RevealItem>
            </div>
            <RevealItem className="flex items-center gap-2 text-overline">
              <Compass className="w-4 h-4 text-gold-500" /> KSA
            </RevealItem>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <RevealItem
                key={i}
                className="metallic-border-top relative p-8 glass hover:bg-navy-700/60 transition-all duration-500 hover:-translate-y-1 group"
              >
                <div className="w-14 h-14 border border-white/10 bg-navy-900 flex items-center justify-center group-hover:border-gold-500/50 transition-colors">
                  <v.Icon className="w-6 h-6 text-gold-500" strokeWidth={1.5} />
                </div>
                <h3 className="mt-5 font-heading font-bold text-white text-xl">{v.t}</h3>
                <p className="mt-2 text-sm text-white/65 leading-relaxed">{v.d}</p>
              </RevealItem>
            ))}
          </div>
        </div>
      </Section>
    </>
  );
}
